import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, FormattedDate, defineMessages, useIntl } from 'react-intl';
import { useMedia } from 'react-media-match';
import { Esi } from '@empiriecom/module-components/Esi';
import Modal from '@empiriecom/module-components/Modal';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { Link } from '@empiriecom/module-components/Link';
import useFetch from '@empiriecom/module-components/hooks/useFetch';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import { FragmentConfiguration } from '@/config';
import { Badge } from '../../utils';
import useUrlParameters from '../../hooks/useUrlParameters';

type ExpressServiceMessageProps = {
  hasExpressService: boolean;
  deadline?: string;
};

const MessageWrapper = styled.div<{ hasBorder: boolean }>`
  margin-bottom: 0.75rem;
  font-size: 0.8125rem;
  ${({ hasBorder = false }) =>
    hasBorder &&
    `border: 1px solid #c5c5c5;
    border-radius: 3px;
    padding: 0.5rem;`}
`;

const Until = styled.div<{ hasBorder: boolean }>`
  font-weight: bold;
  text-align: center;
  ${({ hasBorder = true }) =>
    hasBorder &&
    `border-top: 1px solid #eee;
    margin: 0.5rem 0 0;
    padding-top: 0.5rem;`}
`;

const Countdown = styled.div`
  text-align: center;
`;

const ModalLink = styled(Link)`
  text-decoration: underline;
  color: ${({ theme }) => theme.base.color.text};
  background: none;
  border: 0px none;
  box-shadow: 0px none;
  padding: 0;
  margin: 0;
  display: inline;
  font-size: inherit;
  &&:hover {
    color: ${({ theme }) => theme.greyDark.color.normal};
    border: 0px none;
  }
`;

type ExpressServiceResponse = {
  deliveryDate: string;
  tenant: string;
  countdown: {
    hours: number;
    minutes: number;
    seconds: number;
  };
};

const messages = defineMessages({
  modalHeadline: {
    defaultMessage: '24-Stunden-Lieferung',
    id: 'expressService.modal-headline',
  },
});

/**
 * This component renders a delivery day and a countdown for express service delivery.
 */
export const ExpressServiceMessage: FC<ExpressServiceMessageProps> = ({ hasExpressService }) => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [url, setUrl] = useState<string>(undefined);
  const {
    expressService: { modalContent, apiEndpointUrl },
  } = useConfig<FragmentConfiguration>();
  const intl = useIntl();
  const dispatchGTMEvent = useTracking();
  const fetchConfig = {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'GET',
  };

  // We have to use an extra useEffect to maipulate the url because hooks can't be wrapped with if()
  useEffect(() => {
    if (hasExpressService) {
      setUrl(apiEndpointUrl.replace('<timestamp>', new Date().getTime().toString()));
    }
  }, [hasExpressService]);

  const esiUrl = useUrlParameters(modalContent, undefined, true);

  const { error, response, isLoading } = useFetch<ExpressServiceResponse>({ fetchConfig, url });

  if (!hasExpressService) {
    return <span data-testid="express-service-unavailable" />;
  }

  if (isLoading) {
    return <span data-testid="express-service-loading" />;
  }

  if (error) {
    return <span data-testid="express-service-error" data-error-msg={error.description} />;
  }

  const { countdown, deliveryDate } = response;

  return (
    <MessageWrapper data-testid="express-service" hasBorder={!isMobile}>
      <Until hasBorder={isMobile}>
        <FormattedMessage
          id="expressService.delivery.until"
          defaultMessage="Lieferung am <day>Montag</day>"
          values={{
            day: () => <FormattedDate value={deliveryDate} weekday="long" />,
          }}
        />
      </Until>
      <Countdown>
        <FormattedMessage
          id="expressService.delivery.countdown"
          defaultMessage="Bestelle innerhalb <countdown>5:30</countdown> im <modal>24h Service</modal>"
          values={{
            countdown: () => <Badge greyScale>{`${countdown.hours}:${countdown.minutes}`}</Badge>,
            modal: (msg: string) => (
              <ModalLink
                data-testid="express-modal-link"
                renderAs="span"
                to=""
                onClick={() => {
                  setModalOpen(true);
                  dispatchGTMEvent({
                    event: 'ga_event',
                    eventAction: 'viewLayer',
                    eventCategory: 'noteLayer',
                    eventLabel: 'detailview',
                    eventValue: '24-Stunden-Lieferung',
                  });
                }}
              >
                {msg}
              </ModalLink>
            ),
          }}
        />
      </Countdown>
      <Modal
        isOpen={isModalOpen}
        headline={intl.formatMessage(messages.modalHeadline)}
        onRequestClose={() => setModalOpen(false)}
      >
        <div data-testid="express-modal">
          <Esi src={esiUrl} />
        </div>
      </Modal>
    </MessageWrapper>
  );
};

export default ExpressServiceMessage;
